import React from 'react';
import { Box, Typography, Link } from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import StarIcon from '@mui/icons-material/Star';
import MicIcon from '@mui/icons-material/Mic';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import LaunchIcon from '@mui/icons-material/Launch'; // Icon for external links

const ResearchCard = ({ title, authors, date, finding, findingHighlight, highlight_link, pdf_link,category }) => {
  
  // Helper flag to check if the highlight is a badge or an inline link source
  const isAwardBadge = findingHighlight === "Poster of Distinction";
  const hasInlineSource = findingHighlight && !isAwardBadge && highlight_link;


  const iconMap = {
    abstract: DescriptionIcon,
    media: MicIcon,
    article: MenuBookIcon,
    event: DateRangeIcon, // Fallback default icon handler
  };

  const SelectedIcon = iconMap[category?.toLowerCase()] || DateRangeIcon;


  return (
    <Box sx={{ mb: 6 }}> {/* Spacer between distinct research entries */}
      
      {/* 1. Top Isolated Date Row */}
      {date && (
        <Typography
          variant="body2"
          sx={{
            fontSize: { md: "14px", sm: "13px", xs: "12px" },
            color: "#D80C8C",
            fontWeight: 750,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            mb: 1.5,
            textTransform: "uppercase"
          }}
        >
           <SelectedIcon 
            sx={{ 
              color: '#D80C8C',
              verticalAlign: "middle",   
              marginTop: "-2px"          
            }} 
          />

          {date}
        </Typography>
      )}

      {/* Main Citation Gray Box */}
      <Box
        sx={{
          backgroundColor: "#F8F9FA",
          borderRadius: "20px",
          padding: { md: "30px", sm: "25px", xs: "20px" },
          mb: 2,
        }}
      >
        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontSize: { md: "18px", sm: "16px", xs: "15px" },
            fontWeight: 700,
            color: "#221F73",
            mb: 1.5,
            lineHeight: 1.4,
          }}
        >
          {title}
        </Typography>

        {/* Authors Block (Only displays if text exists) */}
        {authors && (
          <Typography
            variant="body2"
            sx={{
              fontSize: { md: "15px", sm: "14px", xs: "13px" },
              color: "#666",
              mb: 2,
              fontStyle: "italic",
            }}
          >
            {authors}
          </Typography>
        )}

        {/* NEW Source/Publication Link Block */}
        {/* Runs if both findingHighlight and highlight_link are populated, and it isn't an award badge */}
        {hasInlineSource && (
          <Typography
            variant="body2"
            sx={{
              fontSize: { md: "15px", sm: "14px", xs: "13px" },
              color: "#444",
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: "4px"
            }}
          >
            Source:{" "}
            <Link
              href={highlight_link}
              target="_blank"
              rel="noopener"
              sx={{
                color: "#D80C8C",
                fontWeight: 600,
                textDecoration: "underline",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                "&:hover": { color: "#221F73" }
              }}
            >
              {findingHighlight}
              <LaunchIcon sx={{ fontSize: "14px" }} />
            </Link>
          </Typography>
        )}

        {/* 2. Inner Action Row (Duplicated Date + Side-by-side Badge) */}
        {date && (
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              flexWrap: "wrap", 
              gap: "16px",
              mb: pdf_link || highlight_link ? 3 : 0 // Remove bottom margin if no action buttons exist
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { md: "14px", sm: "13px", xs: "12px" },
                color: "#D80C8C",
                fontWeight: 700,
              }}
            >
              {date}
            </Typography>

            {/* Poster of Distinction Award Badge */}
            {isAwardBadge && (
              <Box
                sx={{
                  display: "inline-flex",   
                  alignItems: "center",     
                  gap: "6px",               
                  border: "2px solid #D80C8C", 
                  borderRadius: "5px",      
                  padding: "3px 10px",      
                }}
              >
                <StarIcon sx={{ color: "#D80C8C", fontSize: "16px" }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: "#D80C8C",
                    fontWeight: 700,
                    fontSize: "12px",
                  }}
                >
                  Poster of Distinction
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* 3. Bottom Action Links Row */}
        {(pdf_link || highlight_link) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <Link 
              href={pdf_link || highlight_link} 
              target="_blank"
              rel="noopener"
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: "6px",
                color: "#221F73", 
                fontWeight: 600, 
                fontSize: "14px",
                textDecoration: "underline",
                "&:hover": { color: "#D80C8C" }
              }}
            >
              <PictureAsPdfOutlinedIcon sx={{ fontSize: "18px" }} />
              {pdf_link ? "View Poster" : "View Full Article"}
            </Link>
            
            {pdf_link && (
              <>
                <Typography sx={{ color: "#CCC" }}>|</Typography>
                <Link 
                  href={pdf_link}
                  download
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "6px",
                    color: "#221F73", 
                    fontWeight: 600, 
                    fontSize: "14px",
                    textDecoration: "underline",
                    "&:hover": { color: "#D80C8C" }
                  }}
                >
                  <PictureAsPdfOutlinedIcon sx={{ fontSize: "18px" }} />
                  Download PDF
                </Link>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Findings Box (Conditional) */}
      {finding && (
        <Box
          sx={{
            backgroundColor: "#F0F8FF",
            borderRadius: "15px",
            padding: { md: "20px 30px", sm: "15px 25px", xs: "15px 20px" },
            border: "1px solid #DCE6FB",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: { md: "15px", sm: "14px", xs: "13px" },
              lineHeight: 1.6,
              color: "#333",
            }}
          >
            {finding}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ResearchCard;